import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { Hyperlink } from '../../components/atoms/Hyperlink';
import { Namespace } from '../../translation/namespace';
import { useAppContext } from '../../contexts/AppContext/AppContext';
import IUrl from '../../types/IUrl';
import { asBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { PageLeadDefault } from '../../_lib/components/PageLead';

export enum ErrorType {
  anonymousAccessFailed = 'anonymousAccessFailed',
  authenticationFailed = 'authenticationFailed',
  unknownUser = 'unknownUser',
  pageNotFoundOrNoReadAccess = 'pageNotFoundOrNoReadAccess',
  locked = 'locked',
  internalServerError = 'internalServerError',
}

type ErrorProps = {
  errorType?: ErrorType;
  requestId?: string;
  startPageUrl: IUrl;
  logoutUrl: IUrl;
};

/**
 * Will render a Block witch shows an error message.
 */
export function Error({ errorType, requestId, startPageUrl, logoutUrl }: ErrorProps) {
  const { portalConfiguration } = useAppContext();
  const { t } = useTranslation(Namespace.Error);

  const getStartPageLink = () => {
    if (startPageUrl.url) {
      return (
        <Hyperlink
          to={startPageUrl.url}
          className="error__link"
          // prevent SPA for public error page, see SIBZIEL-5092
          external
        >
          {/* Placeholder */}
        </Hyperlink>
      );
    }

    return null;
  };

  const getLogoutLink = () => {
    if (logoutUrl.url) {
      return (
        <Hyperlink to={logoutUrl.url} className="error__link" routing={false}>
          {/* Placeholder */}
        </Hyperlink>
      );
    }

    return null;
  };

  const getSupportMailtoLink = () => {
    if (portalConfiguration.son_portal_support_email) {
      return (
        <Hyperlink
          to={`mailto:${portalConfiguration.son_portal_support_email}`}
          className="error__link"
          routing={false}
        >
          {/* Placeholder */}
        </Hyperlink>
      );
    }

    return <>{t(`${Namespace.phrases}:contactSupport`)}</>;
  };

  const getErrorType = () => {
    return errorType || ErrorType.pageNotFoundOrNoReadAccess;
  };

  const getLeadText = () => {
    let components = {};
    const startPageLink = getStartPageLink();
    const logoutLink = getLogoutLink();
    const supportMailtoLink = getSupportMailtoLink();

    if (startPageLink) {
      components = { ...components, startPageLink };
    }

    if (logoutLink) {
      components = { ...components, logoutLink };
    }

    if (supportMailtoLink) {
      components = { ...components, supportMailtoLink };
    }

    return (
      <Trans
        i18nKey={`errors.${getErrorType()}.text`}
        i18n={i18n}
        t={t}
        values={{ requestId, startPageLink: t(`${Namespace.glossary}:homePage`), logoutLink: t('logout') }}
        components={components}
      />
    );
  };

  return <PageLeadDefault title={t(`errors.${getErrorType()}.headline`)} leadText={getLeadText()} />;
}

export const ErrorBlock = asBlock(Error, BlockType.error);
